import type { JSX } from 'react';
import type { CoverageSettings } from 'ts/base/routing/search/schema/CoverageSettings';
import { TeamscaleLink } from 'ts/base/routing/TeamscaleLink';
import { CoverageMode } from 'ts/commons/code/CoverageModeSelector';
import { useRenderedCodeOptions } from 'ts/commons/code/RenderedCode.context';
import { InlineCode } from 'ts/commons/components/InlineCode';
import { Links } from 'ts/commons/links/Links';
import { Icon } from 'ts/components/Icon';
import { Tooltip } from 'ts/components/Tooltip';
import type { LineBasedMethodInfo } from 'typedefs/LineBasedMethodInfo';

type MethodInfoProps = {
	method: LineBasedMethodInfo;
};

/**
 * The icon and associated menu for a method in code. Has to be used inside a {@link RenderedCodeContextProvider} as it
 * uses {@link #useRenderedCodeOptions}
 */
export function MethodInfoPopup({ method }: MethodInfoProps): JSX.Element {
	const { project, uniformPath, commit, tgaCoverageSources, coverageSettings } = useRenderedCodeOptions();

	let message;
	if (method.isTestCode) {
		message = 'it is within test code.';
	} else if (method.isTrivial) {
		message = 'it is trivial.';
	} else {
		message = 'it has not been changed in the issue/merge request';
	}
	const coverageMode = coverageSettings.coverageMode;
	let historyCoverageSettings: Partial<CoverageSettings>;
	if (coverageMode === CoverageMode.TEST_GAP) {
		historyCoverageSettings = coverageSettings;
	} else {
		historyCoverageSettings = {
			coverageMode
		};
	}
	return (
		<Tooltip
			data-testid="method-history-popup"
			className="!max-w-[500px]"
			side="bottom"
			align="start"
			trigger={
				<TeamscaleLink
					to={Links.methodHistory(project, uniformPath, {
						commit,
						startOffset: method.location.start,
						endOffset: method.location.end,
						tgaCoverageSources,
						coverageSettings: historyCoverageSettings
					})}
				>
					<Icon color="grey" name="history" size="small" />
				</TeamscaleLink>
			}
		>
			Click to view history of <InlineCode text={method.name} />
			<br />
			{coverageMode === CoverageMode.TEST_GAP &&
				!method.testGapStatus &&
				'This method is not highlighted in this view in Test Gap mode, since ' + message}
		</Tooltip>
	);
}
